/**
 * A module for parsing the raw payloads of JWTs issued by SWS Identity service and
 * exposing their header and payload data.
 *
 * Note: This module does NOT in any way validate the JWTs. It simply exposes their
 * data for the purposes of display and/or driving UI logic.
 *
 * Only SWS web services can validate JWTs issued by the SWS Identity Service.
 */

export interface TokenHeader {
  alg: string
  crit: string[]
  aid: string
  kid: string
  kct: string
}

interface Token {
  header: TokenHeader
  iss: string
  aud: string[]
  sub: string
  iat: Date
  exp: Date
  uid: string
}

export interface RefreshToken extends Token {
  uuid: string
}

export interface AccessToken extends Token {
  app_id: string
  app_name: string
  scopes: AccessScopes
  rtid: string
}

export type AccessScopes = Map<string, Array<string>>

export enum SwsService {
  License = 'license.serato.io',
  Identity = 'id.serato.io',
  Ecom = 'ecom.serato.com',
  Profile = 'profile.serato.com',
  Notifcations = 'notifications.serato.com',
  DigitalAssets = 'da.serato.com'
}

export default function <T extends AccessToken | RefreshToken> (raw: string): T {
  const bits = raw.split('.')
  return Object.assign(
    { header: parseRaw(bits[0]) },
    parseRaw(bits[1]) ?? {}
  ) as T
}

export function hasScope (Scopes: AccessScopes | undefined, service: SwsService, scope: string): boolean {
  if (Scopes !== undefined ) {
    const s = Scopes.get(service)
    if (s !== undefined) return s.includes(scope)
  }
  return false
}

// ignore linter check here because JSON.parse() function in the module is defined to return any
// eslint-disable-next-line
function parseRaw (raw: string): any {
  if (atob(raw)) {
    const serviceNames: Array<string> = Object.values(SwsService)
    const scopes = new Map()
    // ignore linter check here because JSON.parse() function in the module is defined to have a 2nd parameter 
    // `reviver` as any
    // eslint-disable-next-line 
    return JSON.parse(atob(raw), (key: string, value: any): any => {
      if (key === 'iat' || key === 'exp') {
        return new Date(value * 1000)
      } else if (key === 'scopes') {
        return scopes
      } else if (serviceNames.includes(key)) {
        scopes.set(key, value)
      } else {
        return value
      }
    })
  }
}